import type { ReactNode } from 'react';
import { Digits } from './Digits.js';

/**
 * A small, safe Markdown renderer for assistant prose (the model answers with
 * `**bold**`, `### headings`, `-` / `1.` lists, `---` rules, `` `code` ``). It builds
 * React elements only — never HTML strings — so model output cannot inject markup.
 */

const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\s][^*\n]*\*)/g;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    const token = match[0];
    const at = match.index ?? 0;
    if (at > last) out.push(<Digits key={key++}>{text.slice(last, at)}</Digits>);
    if (token.startsWith('**')) {
      out.push(
        <strong key={key++} className="ai-md__strong">
          <Digits>{token.slice(2, -2)}</Digits>
        </strong>,
      );
    } else if (token.startsWith('`')) {
      out.push(
        <code key={key++} className="ai-md__code">
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      out.push(<em key={key++}>{<Digits>{token.slice(1, -1)}</Digits>}</em>);
    }
    last = at + token.length;
  }
  // A marker the typewriter has not closed yet (`**بررسی`) is not shown as raw asterisks.
  if (last < text.length)
    out.push(<Digits key={key++}>{text.slice(last).replace(/\*+/g, '')}</Digits>);
  return out;
}

const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const ITEM = /^(\s*)([-*•]|[\d۰-۹]+[.)])\s+(.*)$/;

interface ListNode {
  readonly ordered: boolean;
  readonly items: { text: string; children: ListNode[] }[];
}

function renderList(list: ListNode, key: number): ReactNode {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag key={key} className="ai-md__list">
      {list.items.map((item, index) => (
        <li key={index}>
          {inline(item.text)}
          {item.children.map((child, childIndex) => renderList(child, childIndex))}
        </li>
      ))}
    </Tag>
  );
}

/** Renders Markdown source (possibly cut mid-way by the typewriter) to React nodes. */
export function renderMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let key = 0;
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length === 0) return;
    const parts: ReactNode[] = [];
    paragraph.forEach((line, index) => {
      if (index > 0) parts.push(<br key={`br${index}`} />);
      parts.push(...inline(line));
    });
    blocks.push(
      <p key={key++} className="ai-md__p">
        {parts}
      </p>,
    );
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      flush();
      continue;
    }
    if (RULE.test(line) && !ITEM.test(line)) {
      flush();
      blocks.push(<hr key={key++} className="ai-md__hr" />);
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      const level = Math.min(heading[1]?.length ?? 3, 4);
      blocks.push(
        <div
          key={key++}
          role="heading"
          aria-level={level + 1}
          className={`ai-md__h ai-md__h--${level}`}
        >
          {inline(heading[2] ?? '')}
        </div>,
      );
      continue;
    }
    if (ITEM.test(line)) {
      flush();
      // Collect the whole list (nested by indentation); a blank line between items is tolerated.
      const stack: { indent: number; list: ListNode }[] = [];
      let root: ListNode | null = null;
      let j = i;
      for (; j < lines.length; j += 1) {
        const current = lines[j] ?? '';
        const match = ITEM.exec(current);
        if (!match) {
          if (current.trim() === '' && ITEM.test(lines[j + 1] ?? '')) continue;
          break;
        }
        const indent = (match[1] ?? '').replace(/\t/g, '  ').length;
        const ordered = /[\d۰-۹]/.test(match[2] ?? '');
        while (stack.length > 0 && indent < (stack[stack.length - 1]?.indent ?? 0)) stack.pop();
        let top = stack[stack.length - 1];
        if (!top || indent > top.indent) {
          const list: ListNode = { ordered, items: [] };
          if (top) top.list.items[top.list.items.length - 1]?.children.push(list);
          else root = list;
          top = { indent, list };
          stack.push(top);
        }
        top.list.items.push({ text: match[3] ?? '', children: [] });
      }
      if (root) blocks.push(renderList(root, key++));
      i = j - 1;
      continue;
    }
    paragraph.push(line);
  }
  flush();
  return blocks;
}
